/**
 * « Verifier dans les fuites connues » depuis la fiche, appele la ou le bouton agit (`check-draft-breach`, `app.ts`).
 *
 * La jauge (`draftPasswordStrength`) ne voit pas qu'un mot de passe « fort » circule deja dans les listes des
 * attaquants ; la Sante du coffre ne le dit qu'apres l'enregistrement, et sur tout le coffre. Bitwarden propose dans
 * sa fiche « Verifier si le mot de passe a ete expose » : meme geste ici, sur le seul mot de passe en cours de saisie.
 *
 * Rien ne part sans le clic : le moteur de fuites galaxie interroge le service en k-anonymat -- seuls les 5 premiers
 * caracteres de l'empreinte SHA-1 partent, jamais le mot de passe ni l'empreinte complete. Le verdict ne nomme jamais
 * le mot de passe ; un service injoignable donne « verification impossible », jamais « absent ».
 */
import { BreachCheckError, checkPwnedPasswords, type BreachCheckOptions } from './breach-check.js';

export type DraftBreachVerdict =
  | { readonly kind: 'exposed'; readonly occurrences: number }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unavailable'; readonly reason: string };

/** Verdict « fuites connues » du mot de passe de la fiche ; `null` sans mot de passe (rien ne part). */
export async function checkDraftPasswordExposure(password: string, check: BreachCheckOptions): Promise<DraftBreachVerdict | null> {
  if (password === '') return null;
  let occurrences: number;
  try {
    occurrences = (await checkPwnedPasswords([password], check)).get(password) ?? 0;
  } catch (cause) {
    if (cause instanceof BreachCheckError) return { kind: 'unavailable', reason: cause.message };
    throw cause;
  }
  return occurrences > 0 ? { kind: 'exposed', occurrences } : { kind: 'absent' };
}

/** Message affiche sous la jauge : nomme le service interroge, jamais le mot de passe. */
export function draftBreachMessage(verdict: DraftBreachVerdict, endpoint: string): string {
  const host = new URL(endpoint).host;
  switch (verdict.kind) {
    case 'exposed':
      return `Expose : ce mot de passe figure ${verdict.occurrences.toLocaleString('fr-FR')} fois dans des fuites connues, les attaquants l’essaient en premier. Generez-en un autre.`;
    case 'absent':
      return `Absent des fuites connues (verifie via ${host}). Cela ne le rend pas robuste : la jauge en juge.`;
    case 'unavailable':
      return `Verification des fuites impossible (${host}) : ${verdict.reason}`;
  }
}
